/**
 * AI CONTEXT
 * Purpose: tag management table (settings subpage).
 * Key deps: tagAtoms/useAllDocs, Tag mapper, AddTagFormModal.
 * Referenced by: Settings tab content.
 * Importance: shared taxonomy used in multiple features.
 * Refactor: separate true loading state from empty collection state.
 */
import { Button } from "antd"
import { useMemo, useState } from "react"

import { tagAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import { buildTableColumns } from "#blocks/Table/buildTableColumns"
import Table2 from "#blocks/Table/Table2"
import { Tag } from "#classes/Tag"
import { TAG_SORTABLE_FIELDS, TAG_TABLE_COLUMNS } from "#constants/tagOptions"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  type TagConstructor,
  type TagForTable,
} from "#customTypes/composedTypes/tagTypes"
import { SETTINGS_KEYS } from "#customTypes/globalTypes"
import AddTagFormModal from "#forms/AddTags/AddTagFormModal"
import { useTableSort } from "#hooks/dataHooks/useTableSort"
import { useAllDocs } from "#hooks/jotai/useSubscription"
import useHasPermission from "#hooks/newHooks/useHasPermission"

const mapTagForTable = (tag: TagConstructor): TagForTable => ({
  ...new Tag(tag).getTagForTable(),
  lastUpdatedAt: tag.lastUpdatedAt,
})

const TagsTable = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedTag, setSelectedTag] = useState<TagConstructor | undefined>()

  const canViewId = useHasPermission(RESOURCES.ID, USER_ACTIONS.READ)

  const { sortField, sortOrder, sortRows, onSortChange } =
    useTableSort<TagForTable>({
      tableKey: SETTINGS_KEYS.TAGS,
      sortableFields: TAG_SORTABLE_FIELDS,
    })

  const handleOpen = () => {
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
    setSelectedTag(undefined)
  }

  const tags = useAllDocs<TagConstructor>(tagAtoms, DOC_TYPES.TAGS)

  const isLoading = tags.length === 0

  const columns = useMemo(
    () =>
      buildTableColumns<TagForTable>(TAG_TABLE_COLUMNS, {
        sortableFields: TAG_SORTABLE_FIELDS,
        showId: canViewId,
      }),
    [canViewId]
  )

  const tagsForTable = useMemo(() => {
    if (!tags) return []

    return sortRows(tags.map(mapTagForTable))
  }, [tags, sortRows])

  const handleRowClick = (selectedTagId: string) => {
    const tag = tags?.find((tag: TagConstructor) => tag.id === selectedTagId)
    if (tag) {
      setSelectedTag(tag)
      handleOpen()
    }
  }

  return (
    <Flex
      col
      gap={12}
      style={{
        alignSelf: "center",
        flex: "1 1 auto",
        minHeight: 0,
        overflow: "hidden",
        width: 600,
        maxWidth: "100%",
      }}
    >
      <h1>Tags</h1>

      <div style={{ flex: "0 0 auto" }}>
        <Button onClick={handleOpen}>Add tag</Button>
      </div>

      {isOpen && (
        <AddTagFormModal onClose={handleClose} initialValues={selectedTag} />
      )}
      <Flex col style={{ flex: "1 1 auto", minHeight: 0, width: "100%" }}>
        <Table2<TagForTable>
          data={tagsForTable}
          columns={columns}
          onRowClick={handleRowClick}
          isLoading={isLoading}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={onSortChange}
          tableProps={{
            scroll: { y: "calc(100vh - 380px)", x: "max-content" },
          }}
        />
      </Flex>
    </Flex>
  )
}

export default TagsTable
